import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError, tRelStatus, tDirection } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { CritBadge, StatusBadge } from "@/components/shared/Badges";
import RelationDialog from "@/components/shared/RelationDialog";
import AlternativeManager from "@/components/shared/AlternativeManager";
import ImpactAnalysis from "@/components/shared/ImpactAnalysis";
import ControlsPanel from "@/components/shared/ControlsPanel";
import RiskPanel from "@/components/shared/RiskPanel";
import ScenariosPanel from "@/components/shared/ScenariosPanel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, Boxes, Server, Building2, ArrowRight, ArrowDownLeft, ArrowUpRight,
  Loader2, Activity, Info, ShieldCheck, Timer, Plus, Pencil, Trash2, Eye, Network, GitBranch,
} from "lucide-react";

const TYPE_ICON = { asset: Server, service: Boxes, organization: Building2 };

function Field({ label, children, mono }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{label}</dt>
      <dd className={`mt-1 text-sm ${mono ? "font-mono" : ""}`}>{children ?? <span className="text-muted-foreground">—</span>}</dd>
    </div>
  );
}

function Section({ title, icon: Icon, children, testid, action }) {
  return (
    <section className="border border-border bg-card rounded-sm" data-testid={testid}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <h2 className="flex items-center gap-2 font-head font-semibold">{Icon && <Icon className="h-4 w-4 text-primary" />} {title}</h2>
        {action}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

function RelationTable({ rows, emptyLabel, onNavigate, writable, onEdit, onView, onDelete, onAlt }) {
  if (!rows.length) return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
  return (
    <div className="border border-border rounded-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent border-border">
            <TableHead>Actif / Service</TableHead>
            <TableHead>Rôle (type)</TableHead>
            <TableHead>Criticité</TableHead>
            <TableHead>Direction</TableHead>
            <TableHead>Statut</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const Icon = TYPE_ICON[r.node.type] || Server;
            const navigable = r.node.type === "asset";
            return (
              <TableRow key={r.edge_id} className="border-border" data-testid={`rel-row-${r.edge_id}`}>
                <TableCell className="font-medium">
                  <button className={`flex items-center gap-2 ${navigable ? "hover:text-primary" : ""}`} onClick={() => navigable && onNavigate(r.node.id)}>
                    <Icon className="h-3.5 w-3.5 text-muted-foreground" /> {r.node.name}
                    {navigable && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
                  </button>
                </TableCell>
                <TableCell><Badge variant="outline" className="rounded-sm font-mono text-xs">{r.type_label}</Badge></TableCell>
                <TableCell><CritBadge value={r.criticality} /></TableCell>
                <TableCell className="text-muted-foreground text-xs">{tDirection(r.direction)}</TableCell>
                <TableCell>{r.node.status ? <StatusBadge value={r.node.status} /> : "—"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" title="Alternatives" onClick={() => onAlt(r.edge_id)} data-testid={`alt-rel-${r.edge_id}`}><GitBranch className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onView(r.edge_id)} data-testid={`view-rel-${r.edge_id}`}><Eye className="h-4 w-4" /></Button>
                    {writable && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(r.edge_id)} data-testid={`edit-rel-${r.edge_id}`}><Pencil className="h-4 w-4" /></Button>}
                    {writable && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => onDelete(r.edge_id)} data-testid={`delete-rel-${r.edge_id}`}><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default function ServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState({ open: false, mode: "create", edgeId: null });
  const [altMgr, setAltMgr] = useState({ open: false, edgeId: null, label: "" });

  const { data, isLoading, isError } = useQuery({
    queryKey: ["service-detail", id],
    queryFn: async () => (await api.get(`/services/${id}/detail`)).data,
  });
  const { data: assets = [] } = useQuery({
    queryKey: ["assets-lite"],
    queryFn: async () => (await api.get("/assets")).data,
  });

  const del = useMutation({
    mutationFn: async (edgeId) => api.delete(`/dependencies/${edgeId}`),
    onSuccess: () => { toast.success("Relation supprimée"); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["service-detail", id] });
    qc.invalidateQueries({ queryKey: ["service-impact", id] });
    qc.invalidateQueries({ queryKey: ["graph"] });
  };
  const onDelete = (edgeId) => { if (window.confirm("Supprimer définitivement cette relation ?")) del.mutate(edgeId); };

  if (isLoading)
    return <AppShell title="Fiche service"><div className="flex items-center gap-2 text-muted-foreground font-mono text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div></AppShell>;
  if (isError || !data)
    return <AppShell title="Fiche service"><p className="text-sm text-muted-foreground">Service introuvable.</p><Button variant="outline" className="mt-4 rounded-sm gap-2" onClick={() => navigate("/services")}><ArrowLeft className="h-4 w-4" /> Retour</Button></AppShell>;

  const s = data.service;
  const at = s.attributes || {};
  const writable = can("dependency:write");

  return (
    <AppShell title={s.name} subtitle="Fiche service critique — NODE métier + RELATIONSHIP"
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={() => navigate(`/dependencies?focus=${id}`)} data-testid="view-subgraph-button"><Network className="h-4 w-4" /> Voir le sous-graphe</Button>
          <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={() => navigate("/services")} data-testid="back-to-services"><ArrowLeft className="h-4 w-4" /> Services</Button>
        </div>
      }>
      <div className="space-y-6" data-testid="service-detail">
        <Section title="Identité" icon={Info} testid="section-identity">
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Field label="Identifiant" mono>{s.id}</Field>
            <Field label="Nom">{s.name}</Field>
            <Field label="Propriétaire métier">{at.owner || null}</Field>
            <Field label="Organisation">{user?.org_name}</Field>
            <Field label="Criticité"><CritBadge value={at.criticality} /></Field>
            <Field label="Statut"><StatusBadge value={at.status} /></Field>
            <div className="col-span-2 md:col-span-4"><Field label="Description">{at.description || null}</Field></div>
          </dl>
        </Section>

        <Section title="Résilience métier" icon={Timer} testid="section-resilience">
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Field label="RTO (heures)" mono>{at.rto_hours}</Field>
            <Field label="RPO (heures)" mono>{at.rpo_hours}</Field>
            <Field label="SLA" mono>{at.sla || null}</Field>
            <Field label="Score de résilience"><span className="font-mono" data-testid="service-resilience-score">{data.resilience_score ?? "Non calculé"}</span></Field>
          </dl>
          {data.resilience_score == null && <p className="text-xs text-muted-foreground mt-4">Score non calculé — le moteur d'évaluation de résilience n'est pas encore configuré (aucune valeur inventée).</p>}
        </Section>

        {/* Résilience structurelle (Vue Impact Service) */}
        <Section title="Résilience structurelle" icon={Network} testid="section-impact">
          <p className="text-xs text-muted-foreground mb-4">Décrit la structure connue des dépendances et redondances (SPOF potentiels, alternatives, redondances vérifiées/partielles, domaines de défaillance communs, dernier test de bascule). Ce n'est <span className="font-semibold">pas</span> le score global de cyber-résilience.</p>
          <ImpactAnalysis serviceId={id} onNavigate={(nid) => navigate(`/assets/${nid}`)} />
        </Section>

        <RiskPanel serviceId={id} />

        <ControlsPanel endpoint={`/services/${id}/controls`} queryKey={["service-controls", id]} />

        <ScenariosPanel endpoint={`/services/${id}/scenarios`} queryKey={["service-scenarios", id]} />

        <Section title={`Actifs supportant le service — ${data.supporting_assets.length}`} icon={ShieldCheck} testid="section-supporting-assets"
          action={writable && <Button size="sm" className="rounded-sm gap-2" onClick={() => setDialog({ open: true, mode: "create", edgeId: null })} data-testid="link-asset-button"><Plus className="h-4 w-4" /> Lier un actif</Button>}>
          <RelationTable rows={data.supporting_assets} writable={writable}
            emptyLabel="Aucun actif associé. Utilisez « Lier un actif »."
            onNavigate={(nid) => navigate(`/assets/${nid}`)}
            onView={(eid) => setDialog({ open: true, mode: "edit", edgeId: eid, view: true })}
            onEdit={(eid) => setDialog({ open: true, mode: "edit", edgeId: eid })}
            onAlt={(eid) => setAltMgr({ open: true, edgeId: eid, label: `Vers ${s.name}` })}
            onDelete={onDelete} />
        </Section>

        <Section title={`Dépendances amont — ${data.incoming.length}`} icon={ArrowDownLeft} testid="section-incoming">
          <RelationTable rows={data.incoming} writable={writable} emptyLabel="Aucune dépendance amont."
            onNavigate={(nid) => navigate(`/assets/${nid}`)} onView={(eid) => setDialog({ open: true, mode: "edit", edgeId: eid })}
            onAlt={(eid) => setAltMgr({ open: true, edgeId: eid, label: "Relation amont" })}
            onEdit={(eid) => setDialog({ open: true, mode: "edit", edgeId: eid })} onDelete={onDelete} />
        </Section>
        <Section title={`Dépendances aval — ${data.outgoing.length}`} icon={ArrowUpRight} testid="section-outgoing">
          <RelationTable rows={data.outgoing} writable={writable} emptyLabel="Aucune dépendance aval."
            onNavigate={(nid) => navigate(`/assets/${nid}`)} onView={(eid) => setDialog({ open: true, mode: "edit", edgeId: eid })}
            onAlt={(eid) => setAltMgr({ open: true, edgeId: eid, label: "Relation aval" })}
            onEdit={(eid) => setDialog({ open: true, mode: "edit", edgeId: eid })} onDelete={onDelete} />
        </Section>

        <Section title="Cycle de vie (NODE · RELATIONSHIP · STATE · EVENT)" icon={Activity} testid="section-lifecycle">
          <p className="text-xs text-muted-foreground">Modèle préparé : <span className="font-mono">Service = NODE métier</span>, <span className="font-mono">Dependency = RELATIONSHIP</span>. STATE/EVENT structurés mais non activés en v0.1 (futur Digital Twin).</p>
        </Section>
      </div>

      <RelationDialog
        open={dialog.open}
        onOpenChange={(v) => setDialog((d) => ({ ...d, open: v }))}
        mode={dialog.mode}
        edgeId={dialog.edgeId}
        fixedNode={{ id: s.id, name: s.name, role: "target" }}
        options={assets.map((a) => ({ id: a.id, name: a.name, type: "asset" }))}
        onSaved={refresh}
      />
      <AlternativeManager
        open={altMgr.open}
        onOpenChange={(v) => setAltMgr((m) => ({ ...m, open: v }))}
        primaryEdgeId={altMgr.edgeId}
        primaryLabel={altMgr.label}
        options={assets.map((a) => ({ id: a.id, name: a.name }))}
        onChanged={refresh}
      />
    </AppShell>
  );
}
